import { useCallback } from 'react';

import { FinanceType } from 'types';
import type { ToleranceBound } from 'types';

import type { EvaluationTemplateDescriptor } from './evaluationTemplateRegistry';
import {
  formatToleranceBound,
  mergeDegreeParam,
  mergeToleranceBound,
  ToleranceBoundRow,
  ToleranceNumberInput,
  ToleranceText,
} from './toleranceParamFields';

// Split out from EvaluationParamsEditor.tsx to avoid a module cycle: the registry reads these
// components eagerly at module-eval time, so the file it reads them from can't depend on the registry.

export const SystemSizeParamsEditor: EvaluationTemplateDescriptor['ParamsEditor'] = ({ params, onCommit }) => {
  const commitPpaBound = useCallback(
    (next: ToleranceBound) => onCommit(mergeToleranceBound(params, FinanceType.ppa, next)),
    [params, onCommit],
  );
  const commitLeaseBound = useCallback(
    (next: ToleranceBound) => onCommit(mergeToleranceBound(params, FinanceType.lease, next)),
    [params, onCommit],
  );

  return (
    <div className="mt-4 flex flex-col gap-4">
      <div className="text-sm font-bold">Tolerance</div>
      <ToleranceBoundRow label="PPA" bound={params.tolerancesByFinanceType?.ppa} onCommit={commitPpaBound} />
      <ToleranceBoundRow label="Lease" bound={params.tolerancesByFinanceType?.lease} onCommit={commitLeaseBound} />
    </div>
  );
};

export const SystemSizeSummary: EvaluationTemplateDescriptor['Summary'] = ({ params }) => {
  const ppa = formatToleranceBound(params?.tolerancesByFinanceType?.ppa);
  const lease = formatToleranceBound(params?.tolerancesByFinanceType?.lease);
  if (!ppa && !lease) return null;
  return (
    <>
      {ppa && <ToleranceText label="PPA" value={ppa} />}
      {lease && <ToleranceText label="Lease" value={lease} />}
    </>
  );
};

export const FirstYearProductionParamsEditor: EvaluationTemplateDescriptor['ParamsEditor'] = ({
  params,
  onCommit,
}) => {
  const commitDefaultBound = useCallback(
    (next: ToleranceBound) => onCommit(mergeToleranceBound(params, 'default', next)),
    [params, onCommit],
  );

  return (
    <div className="mt-4 flex flex-col gap-4">
      <div className="text-sm font-bold">Tolerance</div>
      <ToleranceBoundRow
        label="Default"
        bound={params.tolerancesByFinanceType?.default}
        onCommit={commitDefaultBound}
      />
    </div>
  );
};

export const FirstYearProductionSummary: EvaluationTemplateDescriptor['Summary'] = ({ params }) => {
  const defaultBound = formatToleranceBound(params?.tolerancesByFinanceType?.default);
  if (!defaultBound) return null;
  return <ToleranceText label="Default" value={defaultBound} />;
};

export const ArrayPitchParamsEditor: EvaluationTemplateDescriptor['ParamsEditor'] = ({ params, onCommit }) => {
  const handleToleranceDegreesCommit = useCallback(
    (raw: string) => onCommit(mergeDegreeParam(params, 'toleranceDegrees', raw)),
    [params, onCommit],
  );

  const handleMinDegreesCommit = useCallback(
    (raw: string) => onCommit(mergeDegreeParam(params, 'minDegrees', raw)),
    [params, onCommit],
  );

  const handleMaxDegreesCommit = useCallback(
    (raw: string) => onCommit(mergeDegreeParam(params, 'maxDegrees', raw)),
    [params, onCommit],
  );

  return (
    <div className="mt-4 flex flex-col gap-4">
      <div className="text-sm font-bold">Tolerance</div>

      <div className="flex items-center gap-8">
        <span className="w-64 shrink-0 text-sm text-text-secondary">Tolerance</span>
        <ToleranceNumberInput
          defaultValue={params.toleranceDegrees ?? ''}
          placeholder="Degrees"
          ariaLabel="Tolerance degrees"
          onCommitValue={handleToleranceDegreesCommit}
        />
        <span className="text-xs text-text-tertiary">°</span>
      </div>

      <div className="flex items-center gap-8">
        <span className="w-64 shrink-0 text-sm text-text-secondary">Minimum</span>
        <ToleranceNumberInput
          defaultValue={params.minDegrees ?? ''}
          placeholder="Degrees"
          ariaLabel="Minimum degrees"
          onCommitValue={handleMinDegreesCommit}
        />
        <span className="text-xs text-text-tertiary">°</span>
      </div>

      <div className="flex items-center gap-8">
        <span className="w-64 shrink-0 text-sm text-text-secondary">Maximum</span>
        <ToleranceNumberInput
          defaultValue={params.maxDegrees ?? ''}
          placeholder="Degrees"
          ariaLabel="Maximum degrees"
          onCommitValue={handleMaxDegreesCommit}
        />
        <span className="text-xs text-text-tertiary">°</span>
      </div>
    </div>
  );
};

export const ArrayPitchSummary: EvaluationTemplateDescriptor['Summary'] = ({ params }) => {
  const { toleranceDegrees, minDegrees, maxDegrees } = params ?? {};

  if (toleranceDegrees === undefined && minDegrees === undefined && maxDegrees === undefined) return null;
  return (
    <>
      {toleranceDegrees !== undefined && <span className="body-300 text-text-secondary">±{toleranceDegrees}°</span>}
      {minDegrees !== undefined && <ToleranceText label="Min" value={`${minDegrees}°`} />}
      {maxDegrees !== undefined && <ToleranceText label="Max" value={`${maxDegrees}°`} />}
    </>
  );
};
